import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BookOpen, Plus, PencilSimple, Trash, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataTable, FilterBar } from '../../components/admin/AdminComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['core', 'setup', 'turn', 'action', 'special', 'win', 'combat', 'endgame', 'advanced'];
const EMPTY_RULE = {
  game_slug: '', title: '', category: 'core',
  official_text: '', simplified_text: '', source: '', version: '1.0',
};

export default function AdminRules() {
  const [rules, setRules] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [gameFilter, setGameFilter] = useState('');
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_RULE);
  const [saving, setSaving] = useState(false);

  const fetchGames = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin-console/games?limit=200`, { withCredentials: true });
      setGames(data.games || []);
    } catch {}
  }, []);

  const fetchRules = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (gameFilter) params.append('game', gameFilter);
      params.append('skip', p * 50); params.append('limit', '50');
      const { data } = await axios.get(`${API_URL}/api/admin-console/content/rules?${params}`, { withCredentials: true });
      setRules(data.rules || []); setTotal(data.total || 0);
    } catch {} finally { setLoading(false); }
  }, [search, gameFilter, page]);

  useEffect(() => { fetchGames(); }, [fetchGames]);
  useEffect(() => { fetchRules(); }, [fetchRules]);

  const openAdd = () => {
    setModalMode('add'); setEditingId(null);
    setForm({ ...EMPTY_RULE, game_slug: gameFilter || '' });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode('edit'); setEditingId(row.id);
    setForm({
      game_slug: row.game_slug || '',
      title: row.title || '',
      category: row.category || 'core',
      official_text: row.official_text || '',
      simplified_text: row.simplified_text || '',
      source: row.source || '',
      version: row.version || '1.0',
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (!form.game_slug) { toast.error('Game is required'); return; }
    if (!form.official_text.trim()) { toast.error('Official rule text required'); return; }
    setSaving(true);
    try {
      if (modalMode === 'add') {
        await axios.post(`${API_URL}/api/admin-console/rules`, form, { withCredentials: true });
        toast.success('Rule added');
      } else {
        await axios.put(`${API_URL}/api/admin-console/rules/${editingId}`, form, { withCredentials: true });
        toast.success('Rule updated');
      }
      setModalOpen(false); fetchRules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (row.is_seed) { toast.error('Seed rules cannot be deleted'); return; }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete rule "${row.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin-console/rules/${row.id}`, { withCredentials: true });
      toast.success('Rule deleted'); fetchRules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const columns = [
    {
      key: 'title', label: 'Rule / Section',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <BookOpen size={14} weight="duotone" className="text-[#00F0FF]" />
          <p className="text-[13px] text-white font-medium">{row.title || '—'}</p>
        </div>
      ),
    },
    { key: 'game_slug', label: 'Game', render: v => <span className="text-[12px] text-[#888] capitalize">{(v || '').replace(/-/g, ' ')}</span> },
    { key: 'category', label: 'Category', cellClass: 'hidden md:table-cell', render: v => <span className="text-[12px] text-[#666] capitalize">{v || '—'}</span> },
    { key: 'version', label: 'Version', cellClass: 'hidden lg:table-cell', render: v => <span className="text-[12px] text-[#555]">v{v || '1.0'}</span> },
    {
      key: 'actions', label: '',
      render: (_, row) => (
        <div className="flex gap-1.5">
          <button onClick={() => openEdit(row)}
            className="text-[11px] px-2 py-1 rounded-md bg-white/5 text-[#A1A1AA] hover:bg-white/10 hover:text-white inline-flex items-center gap-1"
            data-testid={`edit-rule-${row.id}`}><PencilSimple size={11} weight="bold" /> Edit</button>
          {!row.is_seed && (
            <button onClick={() => remove(row)}
              className="text-[11px] px-2 py-1 rounded-md bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20 inline-flex items-center gap-1"
              data-testid={`delete-rule-${row.id}`}><Trash size={11} weight="bold" /> Delete</button>
          )}
        </div>
      ),
    },
  ];

  const inp = "w-full bg-[#0a0a14] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#9D4CDD]/50";

  return (
    <div data-testid="admin-rules-page">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Rules</h1>
          <p className="text-[13px] text-[#444] mt-0.5">{total} universal rulesets — linked to games via game_slug</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#9D4CDD] text-white text-xs font-semibold hover:bg-[#7C3AED]"
          data-testid="add-rule-btn">
          <Plus size={14} weight="bold" /> Add Rule
        </button>
      </div>

      <FilterBar searchValue={search} onSearchChange={setSearch} onSearch={() => { setPage(0); fetchRules(0); }}
        searchPlaceholder="Search rules by title or game..." count={total} label="rules">
        <select value={gameFilter} onChange={e => { setGameFilter(e.target.value); setPage(0); }}
          className="text-[11px] bg-white/[0.04] border border-white/[0.08] rounded-md px-2 py-1 text-white"
          data-testid="rule-game-filter">
          <option value="">All games</option>
          {games.map(g => <option key={g.slug} value={g.slug}>{g.name}</option>)}
        </select>
      </FilterBar>

      <DataTable columns={columns} data={rules} loading={loading} emptyMessage="No rules found" emptyIcon={BookOpen}
        rowTestIdPrefix="rule" page={page} pageSize={50} total={total} onPageChange={setPage}
      />

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setModalOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-[#0e0e16] border border-white/[0.08] rounded-2xl p-5 max-h-[90vh] overflow-y-auto" data-testid="rule-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white text-base font-bold">{modalMode === 'add' ? 'Add Rule' : 'Edit Rule'}</h2>
              <button onClick={() => setModalOpen(false)} className="text-[#666] hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase text-[#666] mb-1">Game *</label>
                <select className={inp + ' appearance-none'} value={form.game_slug}
                  onChange={e => setForm({ ...form, game_slug: e.target.value })}
                  data-testid="rule-game-select">
                  <option value="">Select a game…</option>
                  {games.map(g => <option key={g.slug} value={g.slug}>{g.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase text-[#666] mb-1">Title *</label>
                <input className={inp} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="rule-title-input" />
              </div>
              <div>
                <label className="block text-[10px] uppercase text-[#666] mb-1">Category</label>
                <select className={inp + ' appearance-none'} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase text-[#666] mb-1">Official rule text *</label>
                <textarea rows={4} className={inp + ' resize-none'} value={form.official_text}
                  onChange={e => setForm({ ...form, official_text: e.target.value })}
                  data-testid="rule-official-input" />
              </div>
              <div>
                <label className="block text-[10px] uppercase text-[#666] mb-1">Simplified / beginner version</label>
                <textarea rows={2} className={inp + ' resize-none'} value={form.simplified_text}
                  onChange={e => setForm({ ...form, simplified_text: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase text-[#666] mb-1">Source</label>
                  <input className={inp} placeholder="e.g. Rulebook §3" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} />
                </div>
                <div>
                  <label className="block text-[10px] uppercase text-[#666] mb-1">Version</label>
                  <input className={inp} value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-2 pt-3">
                <button onClick={() => setModalOpen(false)}
                  className="flex-1 py-2 rounded-lg border border-white/[0.08] text-[#A1A1AA] text-sm hover:text-white">Cancel</button>
                <button onClick={save} disabled={saving}
                  className="flex-1 py-2 rounded-lg bg-[#9D4CDD] text-white text-sm font-semibold hover:bg-[#7C3AED] disabled:opacity-40"
                  data-testid="save-rule-btn">
                  {saving ? 'Saving…' : modalMode === 'add' ? 'Create' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
